import React, { useState, useEffect, useRef } from "react";
import { Card, Form, Table, Button } from "react-bootstrap";
import { db } from "../../../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";

const PatientSelector = ({ selectedPatient, onSelectPatient }) => {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchRef = useRef(null);

  const loadPatients = async (search = "") => {
    setLoading(true);
    try {
      let patientsQuery;

      if (search) {
        // Search by HN
        patientsQuery = query(
          collection(db, "patients"),
          where("hn", ">=", search),
          where("hn", "<=", search + "\uf8ff"),
          orderBy("hn", "asc"),
          limit(50)
        );
      } else {
        patientsQuery = query(
          collection(db, "patients"),
          orderBy("createdAt", "desc"),
          limit(50)
        );
      }

      const snapshot = await getDocs(patientsQuery);
      const patientList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPatients(patientList);
    } catch (error) {
      console.error("Error loading patients:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const search = searchRef.current.value;
    setSearchTerm(search);
    setHasSearched(true);
    loadPatients(search);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div>
      <h5>Step 1: Select Patient</h5>

      {/* Search */}
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-center">
            <Form.Control
              ref={searchRef}
              type="text"
              placeholder="Search by HN"
              className="mr-2"
              onKeyPress={handleSearchKeyPress}
            />
            <Button variant="primary" onClick={handleSearch}>
              Search
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Selected Patient */}
      {selectedPatient && (
        <Card className="mb-3 border-success">
          <Card.Header className="bg-success text-white">
            <h6 className="mb-0">Selected Patient</h6>
          </Card.Header>
          <Card.Body>
            <div className="row">
              <div className="col-md-6">
                <strong>HN:</strong> {selectedPatient.hn}
                <br />
                <strong>Name:</strong> {selectedPatient.firstnameTh}{" "}
                {selectedPatient.lastnameTh}
                <br />
                <strong>Phone:</strong> {selectedPatient.mobile || "N/A"}
              </div>
              <div className="col-md-6">
                <strong>National ID:</strong>{" "}
                {selectedPatient.nationalId || "N/A"}
                <br />
                <strong>Hospital:</strong>{" "}
                {selectedPatient.refCustomer || "N/A"}
                <br />
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onSelectPatient(null)}
                >
                  Remove Selection
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Patients List - Only show after search */}
      {hasSearched && (
        <Card>
          <Card.Header>
            <h6>Available Patients</h6>
          </Card.Header>
          <Card.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
            {loading ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>HN</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Hospital</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((patient) => (
                    <tr
                      key={patient.id}
                      className={
                        selectedPatient && selectedPatient.id === patient.id
                          ? "table-success"
                          : ""
                      }
                    >
                      <td>
                        <Button
                          variant={
                            selectedPatient && selectedPatient.id === patient.id
                              ? "success"
                              : "outline-primary"
                          }
                          size="sm"
                          onClick={() => onSelectPatient(patient)}
                        >
                          {selectedPatient && selectedPatient.id === patient.id
                            ? "Selected"
                            : "Select"}
                        </Button>
                      </td>
                      <td>{patient.hn}</td>
                      <td>
                        {patient.firstnameTh} {patient.lastnameTh}
                      </td>
                      <td>{patient.mobile || "N/A"}</td>
                      <td>{patient.refCustomer || "N/A"}</td>
                    </tr>
                  ))}
                  {patients.length === 0 && !loading && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No patients found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default PatientSelector;
